import {
  DragDropContext,
  Draggable,
  Droppable,
  DroppableProvided,
  DropResult,
} from '@hello-pangea/dnd';
import { IconButton } from '@mui/material';
import React, { FC, ReactElement, useEffect, useState } from 'react';

import useIsTablet from '../../../../hooks/useIsTablet';
import { ChecklistDataDraft } from '../../../../models/checklist';
import reorder from '../../../../utils/reorder';
import MuiIcon from '../../../MUI/MuiIcon';

import * as Styled from './styled';

type Props = {
  isEditingMode: boolean;
  items: ChecklistDataDraft[];
  requestDelete: (position: number) => void;
  requestReorder: (items: ChecklistDataDraft[]) => void;
};

/**
 * List of checklist items
 *
 * @param props                Props passed to the component
 * @param props.isEditingMode  Whether the component is in editing mode
 * @param props.items          Checklist items added to the draft
 * @param props.requestDelete  Request that an existing item is removed
 * @param props.requestReorder Request that the list is reordered
 *
 * @returns                    Component itself
 */
const ChecklistItems: FC<Props> = ({
  isEditingMode,
  items,
  requestDelete,
  requestReorder,
}) => {
  const isTablet = useIsTablet();
  const [dragItems, setDragItems] = useState(items);

  const shouldEnableDrag = !isTablet || isEditingMode;

  /**
   * The DnD library requires to handle ordering locally,
   * otherwise the list will flicker if the updates are async
   */
  useEffect(() => {
    setDragItems(items);
  }, [items]);

  /**
   * The user has finished dragging an item
   *
   * @param result The data related to dragging finish
   */
  const onDragEnd = (result: DropResult): void => {
    const { destination, source } = result;

    // dropped outside the list
    if (destination === null) {
      return;
    }

    const reordered = reorder(items, source.index, destination.index);
    setDragItems(reordered);
    requestReorder(reordered);
  };

  /**
   * Render the draggable list itself
   *
   * @param provided Metadata needed to make items draggable
   * @returns        The list to inject
   */
  const renderList = (provided: DroppableProvided): ReactElement => {
    const { droppableProps, innerRef: listRef, placeholder } = provided;

    return (
      <Styled.ChecklistItems {...droppableProps} ref={listRef}>
        {dragItems.map(({ text, position }, index) => {
          const itemId = position.toLocaleString();

          return (
            <Draggable
              draggableId={itemId}
              index={index}
              isDragDisabled={!shouldEnableDrag}
              key={itemId}
            >
              {(
                { dragHandleProps, draggableProps, innerRef },
                { isDragging },
              ) => (
                <Styled.ChecklistItem
                  aria-selected={isDragging}
                  data-is-editing={isEditingMode}
                  {...draggableProps}
                  {...(!isTablet && dragHandleProps)}
                  ref={innerRef}
                  role="listitem"
                  tabIndex={0}
                >
                  {shouldEnableDrag && (
                    <IconButton {...(isTablet && dragHandleProps)}>
                      <MuiIcon icon="drag" />
                    </IconButton>
                  )}
                  <Styled.ChecklistItemContent>
                    {text}
                  </Styled.ChecklistItemContent>
                  {shouldEnableDrag && !isDragging && (
                    <IconButton onClick={() => requestDelete(position)}>
                      <MuiIcon color="error" icon="deleteOutlined" />
                    </IconButton>
                  )}
                </Styled.ChecklistItem>
              )}
            </Draggable>
          );
        })}
        {placeholder}
      </Styled.ChecklistItems>
    );
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">{renderList}</Droppable>
    </DragDropContext>
  );
};

export default ChecklistItems;
